import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { clearContentCache, fmtDay } from "@/lib/content";
import { ImageUpload } from "@/components/admin/ImageUpload";

const inp = "h-11 w-full rounded-xl border border-black/15 bg-white px-3 text-sm outline-none focus:border-amber-500";
const area = "w-full rounded-xl border border-black/15 bg-white p-3 text-sm outline-none focus:border-amber-500";
const saveBtn = "rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-black disabled:opacity-50";
const Card = ({ title, text, children, testid }) => (
  <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid={testid}>
    <p className="font-display text-lg font-bold">{title}</p>
    {text && <p className="mt-1 text-xs text-stone-500">{text}</p>}
    <div className="mt-4 space-y-3">{children}</div>
  </div>
);
const F = ({ label, children }) => <label className="block space-y-1"><span className="text-xs text-stone-600">{label}</span>{children}</label>;

const useSave = () => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); clearContentCache(); toast.success(msg); return r; }
    catch (err) { toast.error(formatApiError(err)); return null; }
    finally { setBusy(false); }
  };
  return [busy, run];
};

const HomeEditor = () => {
  const [d, setD] = useState(null);
  const [busy, run] = useSave();
  useEffect(() => { api.get("/content/home").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const t = (k, props = {}) => <input value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} className={inp} data-testid={`home-${k}`} {...props} />;
  return (
    <Card title="Home page" text="Testi della fascia principale, pulsanti e titoli delle sezioni. I link possono essere pagine del sito (es. /catalogo) o indirizzi https://." testid="content-home">
      <ImageUpload label="Immagine di sfondo in alto (vuota = logo del negozio)" value={d.hero_image} onChange={(url) => setD({ ...d, hero_image: url })} testid="home-hero-image" />
      <F label="Testo sotto le carte"><textarea rows={2} value={d.hero_text} onChange={(e) => setD({ ...d, hero_text: e.target.value })} className={area} data-testid="home-hero_text" /></F>
      <div className="grid gap-3 sm:grid-cols-2">
        <F label="Pulsante 1 – testo">{t("cta1_label")}</F><F label="Pulsante 1 – link">{t("cta1_link")}</F>
        <F label="Pulsante 2 – testo (vuoto = nascosto)">{t("cta2_label")}</F><F label="Pulsante 2 – link">{t("cta2_link")}</F>
        <F label="Vetrina – occhiello">{t("featured_eyebrow")}</F><F label="Vetrina – titolo">{t("featured_title")}</F>
        <F label="Nuovi arrivi – occhiello">{t("latest_eyebrow")}</F><F label="Nuovi arrivi – titolo">{t("latest_title")}</F>
        <F label="Riquadro contatti – titolo">{t("contact_title")}</F><F label="Riquadro contatti – testo">{t("contact_text")}</F>
      </div>
      <button disabled={busy} onClick={() => run(() => api.put("/admin/content/home", d), "Home aggiornata")} className={saveBtn} data-testid="home-save">Salva home</button>
    </Card>
  );
};

const AnnouncementEditor = () => {
  const [d, setD] = useState(null);
  const [busy, run] = useSave();
  useEffect(() => { api.get("/content/announcement").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  return (
    <Card title="Avviso in cima al sito" text="La striscia gialla che scorre sopra il menu. Puoi spegnerla, cambiare il messaggio o scegliere se mostrare anche novità ed eventi." testid="content-announcement">
      <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={d.enabled} onChange={(e) => setD({ ...d, enabled: e.target.checked })} data-testid="ann-enabled" />Mostra l'avviso</label>
      <F label="Messaggio"><input value={d.text} onChange={(e) => setD({ ...d, text: e.target.value })} className={inp} maxLength={200} data-testid="ann-text" /></F>
      <F label="Link del messaggio (facoltativo)"><input value={d.link} onChange={(e) => setD({ ...d, link: e.target.value })} placeholder="/spedizioni" className={inp} data-testid="ann-link" /></F>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={d.show_news} onChange={(e) => setD({ ...d, show_news: e.target.checked })} data-testid="ann-news" />Aggiungi in automatico ultimi prodotti ed eventi</label>
      <button disabled={busy} onClick={() => run(() => api.put("/admin/content/announcement", d), "Avviso aggiornato")} className={saveBtn} data-testid="ann-save">Salva avviso</button>
    </Card>
  );
};

const FaqEditor = () => {
  const [items, setItems] = useState(null);
  const [busy, run] = useSave();
  useEffect(() => { api.get("/faqs").then((r) => setItems(r.data.map(({ q, a }) => ({ q, a })))); }, []);
  if (!items) return null;
  const upd = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => { const n = [...items]; [n[i], n[i + dir]] = [n[i + dir], n[i]]; setItems(n); };
  return (
    <Card title="Domande frequenti" text="Appaiono nella home e nella pagina FAQ, in quest'ordine." testid="content-faq">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-black/10 p-3" data-testid={`faq-edit-${i}`}>
          <div className="flex gap-2">
            <input value={it.q} onChange={(e) => upd(i, "q", e.target.value)} placeholder="Domanda" className={inp} data-testid={`faq-edit-q-${i}`} />
            <button disabled={!i} onClick={() => move(i, -1)} className="rounded-lg border px-2 disabled:opacity-30" aria-label="Sposta su" data-testid={`faq-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
            <button disabled={i === items.length - 1} onClick={() => move(i, 1)} className="rounded-lg border px-2 disabled:opacity-30" aria-label="Sposta giù" data-testid={`faq-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="rounded-lg border border-rose-300 px-2 text-rose-600" aria-label="Elimina" data-testid={`faq-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <textarea rows={3} value={it.a} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Risposta" className={`${area} mt-2`} data-testid={`faq-edit-a-${i}`} />
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={() => setItems([...items, { q: "", a: "" }])} className="flex items-center gap-1 rounded-full border-2 border-black px-4 py-2 text-sm font-semibold" data-testid="faq-add"><Plus className="h-4 w-4" />Aggiungi domanda</button>
        <button disabled={busy} onClick={() => run(() => api.put("/admin/faqs", { items }), "FAQ salvate")} className={saveBtn} data-testid="faq-save">Salva FAQ</button>
      </div>
    </Card>
  );
};

const slugOf = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const DocForm = ({ kind, doc, onDone }) => {
  const isPage = kind === "pages";
  const [d, setD] = useState(doc);
  const [busy, run] = useSave();
  const set = (k) => (e) => setD({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    const body = { ...d, slug: d.slug || slugOf(d.title) };
    delete body.id; delete body.created_at; delete body.updated_at; delete body.published_at;
    const r = await run(() => (doc.id ? api.put(`/admin/${kind}/${doc.id}`, body) : api.post(`/admin/${kind}`, body)), isPage ? "Pagina salvata" : "Articolo salvato");
    if (r) onDone();
  };
  const imgKey = isPage ? "image" : "cover";
  return (
    <div className="space-y-3 rounded-xl border-2 border-black/10 bg-[#FFFBF3] p-4" data-testid={`${kind}-form`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <F label="Titolo"><input value={d.title} onChange={set("title")} className={inp} data-testid={`${kind}-title`} /></F>
        <F label={`Indirizzo: ${isPage ? "/pagina/" : "/news/"}${d.slug || slugOf(d.title || "")}`}><input value={d.slug} onChange={(e) => setD({ ...d, slug: slugOf(e.target.value) })} placeholder="automatico dal titolo" className={inp} data-testid={`${kind}-slug`} /></F>
        {isPage ? <F label="Occhiello (facoltativo)"><input value={d.eyebrow} onChange={set("eyebrow")} className={inp} data-testid="pages-eyebrow" /></F>
          : <F label="Riassunto per l'elenco"><input value={d.excerpt} onChange={set("excerpt")} className={inp} maxLength={400} data-testid="posts-excerpt" /></F>}
      </div>
      <ImageUpload label={isPage ? "Immagine (facoltativa)" : "Foto di copertina"} value={d[imgKey]} onChange={(url) => setD({ ...d, [imgKey]: url })} testid={`${kind}-image`} />
      <F label="Testo (lascia una riga vuota per iniziare un nuovo paragrafo)"><textarea rows={10} value={d.body} onChange={set("body")} className={area} data-testid={`${kind}-body`} /></F>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={d.published} onChange={set("published")} data-testid={`${kind}-published`} />Pubblicato</label>
        {isPage && <label className="flex items-center gap-2"><input type="checkbox" checked={d.in_footer} onChange={set("in_footer")} data-testid="pages-in-footer" />Link nel piè di pagina</label>}
        <button disabled={busy || !d.title || !d.body} onClick={save} className={saveBtn} data-testid={`${kind}-save`}>Salva</button>
        <button onClick={onDone} className="text-stone-600 underline" data-testid={`${kind}-cancel`}>Annulla</button>
      </div>
    </div>
  );
};

const EMPTY = { pages: { title: "", slug: "", eyebrow: "", body: "", image: null, published: true, in_footer: true },
  posts: { title: "", slug: "", excerpt: "", body: "", cover: null, published: false } };

const DocList = ({ kind }) => {
  const isPage = kind === "pages";
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [, run] = useSave();
  const load = () => api.get(`/admin/${kind}`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (x) => {
    if (!window.confirm(`Eliminare "${x.title}"?`)) return;
    if (await run(() => api.delete(`/admin/${kind}/${x.id}`), "Eliminato")) load();
  };
  if (!list) return null;
  return (
    <Card title={isPage ? "Pagine informative" : "News / blog"} text={isPage ? "Pagine come Chi siamo, Orari, Come funziona… raggiungibili da /pagina/indirizzo e, se vuoi, dal piè di pagina." : "Articoli pubblicati nella pagina /news. Le bozze non sono visibili ai clienti."} testid={`content-${kind}`}>
      {edit ? <DocForm kind={kind} doc={edit} onDone={() => { setEdit(null); load(); }} />
        : <button onClick={() => setEdit(EMPTY[kind])} className="flex items-center gap-1 rounded-full border-2 border-black px-4 py-2 text-sm font-semibold" data-testid={`${kind}-new`}><Plus className="h-4 w-4" />{isPage ? "Nuova pagina" : "Nuovo articolo"}</button>}
      <ul className="divide-y divide-black/5 text-sm">
        {list.map((x) => (
          <li key={x.id} className="flex flex-wrap items-center gap-3 py-2" data-testid={`${kind}-item-${x.slug}`}>
            <span className="font-semibold">{x.title}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${x.published ? "bg-teal-500/15 text-teal-700" : "bg-stone-200 text-stone-600"}`}>{x.published ? "Pubblicato" : "Bozza"}</span>
            {!isPage && x.published_at && <span className="text-xs text-stone-500">{fmtDay(x.published_at)}</span>}
            {x.published && <a href={`${isPage ? "/pagina" : "/news"}/${x.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-cyan-700 underline" data-testid={`${kind}-view-${x.slug}`}><ExternalLink className="h-3 w-3" />Vedi</a>}
            <button onClick={() => setEdit(x)} className="ml-auto text-xs text-cyan-700 underline" data-testid={`${kind}-edit-${x.slug}`}>Modifica</button>
            <button onClick={() => del(x)} className="text-xs text-rose-600 underline" data-testid={`${kind}-delete-${x.slug}`}>Elimina</button>
          </li>
        ))}
        {!list.length && <li className="py-2 text-stone-500">Ancora niente qui.</li>}
      </ul>
    </Card>
  );
};

export const AdminContent = () => (
  <Tabs defaultValue="home" data-testid="admin-content">
    <TabsList className="h-auto flex-wrap justify-start bg-[#FFFBF3]">
      <TabsTrigger value="home" data-testid="content-tab-home">Home</TabsTrigger>
      <TabsTrigger value="avviso" data-testid="content-tab-announcement">Avviso</TabsTrigger>
      <TabsTrigger value="faq" data-testid="content-tab-faq">FAQ</TabsTrigger>
      <TabsTrigger value="pagine" data-testid="content-tab-pages">Pagine</TabsTrigger>
      <TabsTrigger value="news" data-testid="content-tab-news">News</TabsTrigger>
    </TabsList>
    <TabsContent value="home" className="mt-4"><HomeEditor /></TabsContent>
    <TabsContent value="avviso" className="mt-4"><AnnouncementEditor /></TabsContent>
    <TabsContent value="faq" className="mt-4"><FaqEditor /></TabsContent>
    <TabsContent value="pagine" className="mt-4"><DocList kind="pages" /></TabsContent>
    <TabsContent value="news" className="mt-4"><DocList kind="posts" /></TabsContent>
  </Tabs>
);
